// TODO: This should be later changed to be GameController.tsx

import {
  CommunicationDataType,
  GameDataDefinition,
  PlayerModel,
} from '@toilet-paper-games/core';
import { autorun, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';

import { IFrameLoader, IFrameLoaderRef } from '@/common/components/iframe/IFrameLoader';
import { NetworkDataType } from '@/common/models/NetworkDataTransfers';
import { CommunicationHandlerAppController } from '@/controller/models/CommunicationHandlerAppController';
import { GameInfo } from '@/types';
import { fullSize } from '@toilet-paper-games/ui';

import { BaseViewModel, useViewModelConstructor } from '@/common/utils/mobx/ViewModel';
import { makeSimpleAutoObservable } from '@/common/utils/mobx/mobx';
import {
  ControllerAppModel,
  useControllerAppModel,
  useControllerNetworkHandler,
} from '../models/ControllerAppModel';
import { ControllerNetworkHandler } from '../models/ControllerNetworkHandler';
import { ControllerInjectedGameDataHandler } from '../models/ControllerInjectedGameDataHandler';

export interface ControllerGamePlayerViewModelProps {
  game: GameInfo;
  appModel: ControllerAppModel;
  iFrameLoaderRef: React.RefObject<IFrameLoaderRef>;
  networkHandler: ControllerNetworkHandler;
  lobbyGame: boolean;
  updateRefreshCount: () => void;
}

export class ControllerGamePlayerViewModel extends BaseViewModel<ControllerGamePlayerViewModelProps> {
  communicationHandler: CommunicationHandlerAppController<GameDataDefinition> =
    new CommunicationHandlerAppController(this);

  controllerInjectedGameDataHandler: ControllerInjectedGameDataHandler;

  constructor(props: ControllerGamePlayerViewModelProps) {
    super(props);
    makeSimpleAutoObservable(this, {}, { autoBind: true });

    this.controllerInjectedGameDataHandler = new ControllerInjectedGameDataHandler(
      this.props.game,
      this.props.appModel,
      this.props.lobbyGame,
    );
  }

  setupControllerInjectedGameDataHandler() {
    return this.controllerInjectedGameDataHandler.setup();
  }

  reset() {
    this.props.updateRefreshCount();
  }

  setupCommunicationHandler() {
    const iFrameLoader = this.props.iFrameLoaderRef.current;

    if (!iFrameLoader) {
      throw new Error('No iframe loader');
    }

    const iframe = iFrameLoader.iframe;

    if (!iframe) {
      throw new Error('No iframe');
    }

    const messenger = (data: unknown) => {
      if (iframe.contentWindow) {
        iframe.contentWindow.postMessage(data, '*');
      }
    };

    this.communicationHandler.setupMessenger(messenger);

    return () => {};
  }

  setupCommunicationListeners() {
    const appModel = this.props.appModel;
    const communicationHandler = this.communicationHandler;
    const networkHandler = this.props.networkHandler;
    const iFrameLoader = this.props.iFrameLoaderRef.current;

    if (!iFrameLoader) {
      throw new Error('No iframe loader');
    }

    const refreshListenerDestroyer = networkHandler.addDataListener(() => {
      iFrameLoader.reload();
      this.reset();
    }, NetworkDataType.REFRESH);

    const hosterPingListenerDisposer = networkHandler.addDataListener((message) => {
      communicationHandler.sendToFrame({
        type: CommunicationDataType.PING_HOSTER,
        data: {
          hosterTime: message.data.hosterTime,
          id: message.data.id,
          playerId: message.data.userId,
          timeSinceStart: message.data.timeSinceStart,
        },
      });
    }, NetworkDataType.PING_H2C);

    const hosterPongListenerDisposer = networkHandler.addDataListener((message) => {
      communicationHandler.sendToFrame({
        type: CommunicationDataType.PONG_HOSTER,
        data: {
          hosterTime: message.data.hosterTime,
          id: message.data.id,
          pingMs: message.data.pingMs,
          playerId: message.data.userId,
          timeSinceStart: message.data.timeSinceStart,
        },
      });
    }, NetworkDataType.PONG_H2C);

    const dataListenerDestroyer = networkHandler.addDataListener((listener) => {
      communicationHandler.sendToFrame({
        type: CommunicationDataType.GAME_ACTION_RESPONSE_CONTROLLER,
        data: { payload: listener.data },
      });
    }, NetworkDataType.GAME_ACTION);

    let startupAutoRunDisposer: (() => void) | undefined;
    const startupListener = communicationHandler.addAppMessageListener((message) => {
      const hostConnection = networkHandler.hostConnection;

      if (!hostConnection) {
        throw new Error('No host connection');
      }

      if (startupAutoRunDisposer) startupAutoRunDisposer();

      startupAutoRunDisposer = autorun(() => {
        communicationHandler.sendToFrame({
          type: CommunicationDataType.AppData_CONTROLLER,
          data: toJS(this.controllerInjectedGameDataHandler.injectedGameData),
        });
      });
    }, CommunicationDataType.INIT_GAME_CONTROLLER);

    const readyListener = communicationHandler.addAppMessageListener(({ data }) => {
      this.controllerInjectedGameDataHandler.ready = data.ready;

      const hostConnection = networkHandler.hostConnection;

      if (!hostConnection) {
        throw new Error('No host connection');
      }

      hostConnection.send({
        type: NetworkDataType.GAME_READY_STATUS_CONTROLLER_C2H,
        data: {
          ready: data.ready,
        },
      });
    }, CommunicationDataType.READY_STATUS_CONTROLLER);

    const gameStorageUpdateListener = communicationHandler.addAppMessageListener(
      ({ data }) => {
        this.controllerInjectedGameDataHandler.updateGameStorage(data.key, data.value);

        communicationHandler.sendToFrame({
          type: CommunicationDataType.UPDATED_GAME_STORAGE_CONTROLLER_P2G,
          data: { key: data.key, value: data.value },
        });
      },
      CommunicationDataType.SET_GAME_STORAGE_CONTROLLER_G2P,
    );

    const globalSettingsUpdateListener = communicationHandler.addAppMessageListener(
      ({ data }) => {
        appModel.updateControllerGlobalSettings(data.key, data.value);

        communicationHandler.sendToFrame({
          type: CommunicationDataType.UPDATED_GLOBAL_SETTING_CONTROLLER_P2G,
          data: { key: data.key, value: data.value } as any,
        });
      },
      CommunicationDataType.SET_GLOBAL_SETTING_CONTROLLER_G2P,
    );

    return () => {
      refreshListenerDestroyer();
      dataListenerDestroyer();
      startupListener.destroy();
      if (startupAutoRunDisposer) {
        startupAutoRunDisposer();
      }

      readyListener.destroy();
      hosterPingListenerDisposer();
      hosterPongListenerDisposer();

      gameStorageUpdateListener.destroy();
      globalSettingsUpdateListener.destroy();
    };
  }

  onMessage(message: string) {
    this.communicationHandler.messageHandler(message);
  }
}

export interface ControllerGamePlayerProps {
  game: GameInfo;
  lobbyGame: boolean;
}

export const ControllerGamePlayer = observer((props: ControllerGamePlayerProps) => {
  const { game, lobbyGame } = props;

  const appModel = useControllerAppModel();
  const networkHandler = useControllerNetworkHandler();

  const iFrameLoaderRef = useRef<IFrameLoaderRef>(null);

  const [refreshCount, setRefreshCount] = useState(0);

  const updateRefreshCount = () => {
    setRefreshCount((count) => count + 1);
  };

  const viewModel = useViewModelConstructor(ControllerGamePlayerViewModel, {
    game,
    appModel,
    iFrameLoaderRef,
    networkHandler,
    lobbyGame,
    updateRefreshCount,
  });

  useEffect(() => {
    return viewModel.setupControllerInjectedGameDataHandler();
  }, [viewModel, refreshCount]);

  useEffect(() => {
    const setupHandlerDisposer = viewModel.setupCommunicationHandler();
    const setupListenerDisposer = viewModel.setupCommunicationListeners();

    return () => {
      setupHandlerDisposer();
      setupListenerDisposer();
    };
  }, []);

  return (
    <div css={[fullSize]}>
      <IFrameLoader
        ref={iFrameLoaderRef}
        title=""
        onMessage={viewModel.onMessage}
        src={game.endpoints.controller}
      />
    </div>
  );
});
